import { z } from 'zod';

// Enums and primitives
export const SegregationScopeSchema = z.enum(['ALL_FLOWS', 'PER_FLOW']);
export const TimeoutConditionSchema = z.enum(['DUE_DATE_IN', 'APPROVAL_PENDING_FOR']);
export const TimeoutUnitSchema = z.enum(['HOURS', 'DAYS']);
export const TimeoutActionSchema = z.enum([
  'NOTIFY_PENDING_APPROVERS',
  'ESCALATE_TO_MASTER',
  'NOTIFY_MASTER_URGENT',
]);
export const RequiredBillFieldSchema = z.enum([
  'GL_ACCOUNT',
  'MANAGERIAL_ACCOUNT',
  'MANAGERIAL_DATE',
  'DEPARTMENT',
  'COST_CENTER',
  'DESCRIPTION',
  'DUE_DATE',
  'COMPETENCY_DATE',
  'ISSUE_DATE',
  'VENDOR',
  'PAYMENT_METHOD',
  'TOTAL_AMOUNT',
  'SOURCE',
  'ATTACHMENT',
]);

// Complex objects
export const TimeoutRuleSchema = z.object({
  id: z.string().uuid(),
  enabled: z.boolean(),
  title: z.string().min(1).max(200),
  condition: TimeoutConditionSchema,
  threshold: z.number().int().positive(),
  unit: TimeoutUnitSchema,
  action: TimeoutActionSchema,
});

export const ApprovalGlobalSettingsSchema = z.object({
  requestApprovalByDefault: z.boolean(),
  segregationEnabled: z.boolean(),
  segregationScope: SegregationScopeSchema,
  masterApproverUserIds: z.array(z.string().uuid()),
  timeoutRules: z.array(TimeoutRuleSchema),
  requiredFields: z.record(RequiredBillFieldSchema, z.boolean()),
  requiredCustomFieldIds: z.array(z.string().uuid()),
  /**
   * Tenant opt-in: when `true`, bills created automatically by an ingestion
   * pipeline (Conta Simples card attachments, recurring invoices, etc.) are
   * automatically advanced `SUBMITTED → PENDING_APPROVAL` so the configured
   * approval workflow runs — without a user having to submit each bill by hand.
   *
   * This only applies to bills whose classification came from a deterministic
   * source: a workflow rule, fast-path lookup of the most recent USER
   * classification, reuse of the vendor's approved history, or an AI suggestion
   * that matches a classification already approved for the vendor
   * (`ai-confirmed`). AI-only classifications with no approved precedent stay in
   * `SUBMITTED` for manual review.
   *
   * It does NOT force approval: the canonical `transitionStatus` flow decides
   * whether the bill is auto-approved (no workflow matched) or stays
   * `PENDING_APPROVAL` awaiting approvers (a workflow matched).
   *
   * Defaults to `false`: tenants must explicitly opt in. The previous key
   * `autoApproveCardPreClassifiedBills` is read for back-compat during
   * normalization.
   */
  autoSubmitCreatedBillsToApproval: z.boolean().default(false),

  /**
   * Credit card policy: when set to a positive number, card transactions above
   * this amount (in BRL) that have no attachment are flagged on the fast-track
   * classification page and will be blocked from GL posting.
   *
   * `null` means the rule is disabled.
   */
  creditCardRequireAttachmentAboveAmount: z.number().positive().nullable().optional(),
});

// Type exports
export type SegregationScope = z.infer<typeof SegregationScopeSchema>;
export type TimeoutCondition = z.infer<typeof TimeoutConditionSchema>;
export type TimeoutUnit = z.infer<typeof TimeoutUnitSchema>;
export type TimeoutAction = z.infer<typeof TimeoutActionSchema>;
export type RequiredBillField = z.infer<typeof RequiredBillFieldSchema>;
export type TimeoutRule = z.infer<typeof TimeoutRuleSchema>;
export type ApprovalGlobalSettings = z.infer<typeof ApprovalGlobalSettingsSchema>;
